<template>
  <div>
    <div class="p-box">
      <Loading v-if="loading" />
      <template v-else>
        <div>
          <header class="p-box__header">
            <div class="p-filtersA">
              <div class="p-filtersA__left">
                <CompaniesMediaLibrariesStoreInfo :name="store.attributes.name" :image-url="store.attributes.brand_image_url" />
              </div>
              <div class="p-filtersA__right">
                <div class="sortForm c-buttonSort">
                  <button
                    class="c-buttonSort__item"
                    :class="`is-${sortOrder}`"
                    type="button"
                    :disabled="loading"
                    @click="sort()"
                    v-text="$t('labels.sort.date')"
                  />
                </div>
                <div v-if="showMoreThanOperatorAuthority" class="c-select">
                  <select v-model="addAction" @change="handleSelectAction($event.target.value)">
                    <option value="" disabled>{{ $t('pages.CompaniesLocationStore.mediaLibraries.addMediaLibrary') }}</option>
                    <option value="selectMediaLibraries">
                      {{ $t('pages.CompaniesLocationStore.mediaLibraries.selectMediaLibraries') }}
                    </option>
                    <option value="upload">
                      {{ $t('pages.CompaniesLocationStore.mediaLibraries.uploadMediaLibrary') }}
                    </option>
                  </select>
                </div>
                <div>
                  <router-link :to="{ name: 'CompaniesLocationStore', params: { company_uuid: companyUuid, store_uuid: storeUuid } }" class="c-button__item c-button__item--outline">
                    {{ $t('pages.CompaniesLocationStore.mediaLibraries.linkLocationStore') }}
                  </router-link>
                </div>
              </div>
            </div>
          </header>
          <div class="p-box__body">
            <Loading v-show="loading" />
            <template v-if="!loading">
              <div v-if="storeMediaLibraries" class="p-photos">
                <div class="p-photos__total_count">
                  {{ $t('pages.CompaniesLocationStore.mediaLibraries.storeMediaLibrariesTotalCount', { number: numberLocaleString(storeMediaLibrariesMeta.totalCount) }) }}
                </div>
                <div class="p-photos__items">
                  <div v-for="mediaLibrary in storeMediaLibraries"
                       :key="mediaLibrary.id"
                       class="p-photos__item"
                  >
                    <div>
                      <router-link :to="{ name: 'CompaniesLocationStoreMediaLibrary', params: { company_uuid: companyUuid, store_uuid: storeUuid, id: mediaLibrary.id } }">
                        <div
                          class="p-photos__image"
                        >
                          <div :style="{ 'background-image': `url(${ mediaLibrary.mediaImageUrl })` }" />
                        </div>
                      </router-link>
                      <div class="p-photos__name">
                        <TextMiddleEllipsis :text="mediaLibrary.fileName" :end-offset="7" />
                      </div>
                      <div class="p-photos__summary">
                        <p class="statusMsg" :class="{ 'statusMsg__error': mediaLibrary.status === 'error' }">
                          <span :class="`statusMark__${mediaLibrary.status}`" class="statusMark" />
                          {{ $t('pages.CompaniesLocationStore.mediaLibraries.status.' + mediaLibrary.status) }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else v-text="$t('messages.noData')" />
              <div v-if="storeMediaLibrariesMeta.totalPages" class="p-controller">
                <div class="p-controller__count">
                  <p class="p-controller__count__name">
                    {{ $t('labels.perPageCount') }}
                  </p>
                  <div class="p-controller__count__select">
                    <select :value="displayNumber" @change="changeDisplayNumber($event)">
                      <option v-for="item in 5" :key="item" :value="item * 50">{{ item * 50 }}</option>
                    </select>
                  </div>
                  <p class="p-controller__count__current">{{ startDataNumber }}-{{ endDataNumber }}</p>
                </div>
                <div class="p-controller__navi">
                  <button
                    type="button"
                    :disabled="loading || currentPage === 1"
                    class="p-controller__navi__prev"
                    @click="goPrevPage"
                  >
                    <Icon name="arrowLeft" />
                  </button>
                  <button
                    type="button"
                    :disabled="loading || currentPage >= storeMediaLibrariesMeta.totalPages"
                    class="p-controller__navi__next"
                    @click="goNextPage"
                  >
                    <Icon name="arrowRight" />
                  </button>
                </div>
              </div>
            </template>
          </div>
        </div>
      </template>
      <SelectMediaLibrariesModal
        v-if="modal"
        :media-libraries="mediaLibraries"
        :select-media-library="mediaLibrary"
        :media-library-loading="mediaLibraryLoading"
        :media-libraries-loading="mediaLibrariesLoading"
        :sort-key="modalSortKey"
        @close="handleModalClose"
        @submit="handleAddMediaLibraries"
        @read-select-media-library="handleReadMediaLibrary"
        @change-sort="handleChangeSort"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapState, mapGetters } from 'vuex'
import { mapStores } from 'pinia'
import Loading from '../components/Loading'
import Icon from '../components/Icon'
import SelectMediaLibrariesModal from '../components/CompaniesMediaLibraries/SelectMediaLibrariesModal'
import CompaniesMediaLibrariesStoreInfo from '../components/CompaniesMediaLibraries/StoreInfo'
import { toNumberLocaleString } from '../modules/localeString'
import TextMiddleEllipsis from '../components/TextMiddleEllipsis'
import { useLocationStoreSetupsStore } from '../domains/locationStore/stores'

export default {
  name: 'CompaniesLocationStoreMediaLibraries',
  components: {
    Loading,
    Icon,
    SelectMediaLibrariesModal,
    CompaniesMediaLibrariesStoreInfo,
    TextMiddleEllipsis
  },
  emits: ['update-title'],
  data() {
    return {
      currentPage: 1,
      sortOrder: 'desc',
      sortKey: 'created_at',
      modalSortKey: 'store_count',
      addAction: '',
      title: '',
      modal: false,
    }
  },
  computed: {
    ...mapState({
      mediaLibraries: state => state.companiesMediaLibraries.data,
      mediaLibrariesLoading: state => state.companiesMediaLibraries.loading,
      storeMediaLibraries: state => state.companiesStoreMediaLibraries.data,
      storeMediaLibrariesMeta: state => state.companiesStoreMediaLibraries.meta,
      storeMediaLibrariesLoading: state => state.companiesStoreMediaLibraries.loading,
      mediaLibrary: state => state.companiesMediaLibrary.data,
      mediaLibraryLoading: state => state.companiesMediaLibrary.loading,
      displayNumber: state => state.filters.displayNumber,
      store: state => state.companiesLocationStore.data,
      storeLoading: state => state.companiesLocationStore.loading,
    }),
    ...mapGetters({
      showMoreThanOperatorAuthority: 'companiesAccessibleApi/showMoreThanOperatorAuthority',
      hasYahooPlaceStore: 'companiesCompany/hasYahooPlaceStore',
    }),
    ...mapStores(useLocationStoreSetupsStore),
    companyUuid() { return this.$route.params.company_uuid },
    storeUuid() { return this.$route.params.store_uuid },
    loading() { return this.storeMediaLibrariesLoading || this.storeLoading },
    startDataNumber() {
      return (this.currentPage - 1) * this.displayNumber + 1
    },
    endDataNumber() {
      return this.startDataNumber + this.storeMediaLibraries.length - 1
    },
    isConnectedYahooPlace() { return this.store.attributes.connected_yahoo_place },
    storeMediaLibrariesQuery() {
      return {
        page: this.currentPage,
        per: Number(this.displayNumber),
        sort_key: this.sortKey,
        sort_order: this.sortOrder,
      }
    },
    mediaLibrariesQuery() {
      return {
        sort_key: this.modalSortKey,
        sort_order: 'desc',
        exclude_store_uuids: [this.storeUuid]
      }
    },
  },
  watch: {
    title(newTitle) {
      this.$emit('update-title', newTitle)
    }
  },
  async created() {
    this.readStoreMediaLibraries({ storeUuid: this.storeUuid, payload: this.storeMediaLibrariesQuery })
    this.readCompaniesMediaLibraries(this.mediaLibrariesQuery)
    this.initMediaLibrary()
    await this.readStore({ company_uuid: this.companyUuid, store_uuid: this.storeUuid })
    this.title = `${this.store.attributes.name} - ${this.$t('pages.CompaniesLocationStoreMediaLibraries.title')}`

    if (this.isConnectedYahooPlace) {
      await this.locationStoreSetupsStore.readStoreSetups(this.companyUuid, [this.storeUuid])
    }
  },
  methods: {
    ...mapActions({
      readStore: 'companiesLocationStore/readStore',
      readCompaniesMediaLibraries: 'companiesMediaLibraries/readCompaniesMediaLibraries',
      readStoreMediaLibraries: 'companiesStoreMediaLibraries/readMediaLibraries',
      readMediaLibrary: 'companiesMediaLibrary/readMediaLibrary',
      setDisplayNumber: 'filters/setDisplayNumber',
      setPeriod: 'filters/setPeriod',
      setMediaLibraryIds: 'companiesMediaLibraries/setMediaLibraryIds',
      createStoreMediaLibraries: 'companiesStoreMediaLibraries/createStoreMediaLibraries'
    }),
    ...mapMutations({
      initMediaLibrary: 'companiesMediaLibrary/initMediaLibrary',
    }),
    getInitialYahooConnection(selectServices) {
      if (!this.hasYahooPlaceStore || !this.isConnectedYahooPlace) return false
      if (!selectServices.includes('yahoo_map')) return false
      const setup = this.locationStoreSetupsStore.findStoreSetup(this.storeUuid)
      return setup ? !setup.yahooMediaLibrary : false
    },
    handleScrollTopAndSubmit() {
      window.scrollTo({ top: 0 })
      this.handleSubmit()
    },
    handleResetPageAndSubmit() {
      this.currentPage = 1
      this.handleSubmit()
    },
    handleSubmit() {
      this.readStoreMediaLibraries({ storeUuid: this.storeUuid, payload: this.storeMediaLibrariesQuery })
    },
    handleChangeSort(sortKey) {
      this.modalSortKey = sortKey
      this.readCompaniesMediaLibraries(this.mediaLibrariesQuery)
    },
    async handleSelectAction(val) {
      switch (val) {
      case 'upload':
        this.$router.push({
          name: 'CompaniesLocationStoreMediaLibraryUpload',
          params: { company_uuid: this.companyUuid , store_uuid: this.storeUuid}
        })
        return
      case 'selectMediaLibraries':
        this.modal = true
        return
      }
    },
    handleReadMediaLibrary(mediaLibraryId) {
      this.readMediaLibrary(mediaLibraryId)
    },
    async handleAddMediaLibraries({ mediaLibraryIds, selectServices, selectCategory }) {
      if (!this.getInitialYahooConnection(selectServices) || window.confirm(this.$t('models.mediaLibraryUpload.initialYahooConnection'))) {
        this.handleModalClose()
        const payload = { media_library_ids: mediaLibraryIds, services: selectServices, gmb_category: selectCategory }
        await this.createStoreMediaLibraries({ storeUuid: this.storeUuid, payload: payload })
        this.handleResetPageAndSubmit()
        this.readCompaniesMediaLibraries(this.mediaLibrariesQuery)
      }
    },
    handleModalClose() {
      this.initMediaLibrary()
      this.addAction = ''
      this.modal = false
    },
    sort() {
      this.sortOrder = this.sortOrder === 'desc' ? 'asc' : 'desc'
      this.handleScrollTopAndSubmit()
    },
    goPrevPage() {
      --this.currentPage
      this.handleScrollTopAndSubmit()
    },
    goNextPage() {
      ++this.currentPage
      this.handleScrollTopAndSubmit()
    },
    numberLocaleString(val) {
      return toNumberLocaleString(val)
    },
    /**
     * 表示件数の変更
     * @param  { Object } e イベントオブジェクト
     * @return { Void }
     */
    changeDisplayNumber(e) {
      this.setDisplayNumber(e.target.value)
      this.handleResetPageAndSubmit()
    }
  },
}
</script>

<style lang="scss" scoped>
@import "../../../stylesheets/biz_spa/foundation/_var.scss";
@import "../../../stylesheets/biz_spa/foundation/_mixin.scss";
.p-filtersA__right {
  align-items: flex-start;
}
.noMediaBox p {
  margin-bottom: 16px;
}
.fontBold {
  font-weight: bold;
}
.statusMsg__error {
  color: $color-accent-primary;
}
.p-photos__summary {
  padding-bottom: 0;
  p {
    margin: 8px 0;
  }
}
.p-photos__item {
  @include mq(xs) {
    width: calc(100% / 2);
  }
  @include mq(md) {
    width: calc(100% / 3);
  }
  @include mq(xl) {
    width: calc(100% / 4);
  }
  .p-photos__image {
    padding-bottom: 85%;
    div {
      width: 85%;
    }
  }
}
.p-photos__name {
  margin-top: 10px;
}
.statusMark {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  &__error {
    background: $color-accent-primary;
  }
  &__complete {
    background: $complete-color;
  }
  &__progress {
    background: #D2D5DB;
  }
}
.c-button__item--outline {
  font-weight: normal;
}
</style>
